import React, { FC } from "react";
import { useSelector } from "react-redux";
import { ResourceInfoDc, ResourceTypeLink } from "@evergis/api";
import { Dialog, DialogContent } from "@evergis/ui";

import { Content } from "../styled";
import { Actions } from "./Actions";
import { Content as DeleteResourcesContent } from "./Content";
import { Loading } from "./Loading";
import { Title } from "./Title";
import { getUsername } from "../../../../ducks/user";
import { getCurrentTab } from "../../../../ducks/location/selectors";
import { useDialogReset } from "../../../../hooks/useDialogs";
import { useDeleteResources } from "../hooks/useDeleteResources";
import { useDeleteResourcesChoice } from "../hooks/useDeleteResourcesChoice";
import { useResourceType } from "../../../../hooks/useResourceType";
import { DeleteResources, isLayersWithDeps, isProjectsWithDeps, resolveResources } from "../resolveResources";

export const DIALOG_STYLE = {
  maxWidth: "36rem",
};

interface DialogProps {
  resources?: DeleteResources;
  loading?: boolean;
  closeDialogOnDelete?: boolean;
}

export const DeleteResourcesDialog: FC<DialogProps> = ({ resources, loading, closeDialogOnDelete }) => {
  const cancelDelete = useDialogReset();
  const [catalogResourceType] = useResourceType();
  const [ableToDelete, toggle] = useDeleteResourcesChoice();
  const owner = useSelector(getUsername);
  const { projects, layers, tables } = resolveResources(resources, owner);
  const locationTab = useSelector(getCurrentTab);
  const resourceType = locationTab
    ? catalogResourceType
    : isLayersWithDeps(resources)
    ? ResourceTypeLink.Layer
    : isProjectsWithDeps(resources)
    ? ResourceTypeLink.Project
    : resources
    ? ResourceTypeLink.Table
    : undefined;
  const ignoreLayers = !ableToDelete.layers && resourceType !== ResourceTypeLink.Layer;
  const ignoreTables = !ableToDelete.tables && resourceType !== ResourceTypeLink.Table;
  const projectInfo = getInfo(projects);
  const layerInfo = getInfo(layers, ignoreLayers);
  const tableInfo = getInfo(tables, ignoreTables);

  const [deleteResources, onDeleteLoading] = useDeleteResources(
    { projects: projectInfo, layers: layerInfo, tables: tableInfo },
    resourceType,
    ableToDelete,
    true,
  );

  return (
    <Dialog isOpen modal onCloseRequest={cancelDelete} maxWidth={DIALOG_STYLE.maxWidth}>
      <Title type={resourceType} />
      <DialogContent>
        <Content>
          {onDeleteLoading ? (
            <Loading type={resourceType} isLoading={loading} isDeleting={onDeleteLoading} />
          ) : (
            <DeleteResourcesContent
              type={resourceType}
              projects={projects}
              layers={layers}
              tables={tables}
              checked={ableToDelete}
              toggle={toggle}
            />
          )}
        </Content>
      </DialogContent>
      <Actions
        deleteResources={() => {
          deleteResources();

          if (closeDialogOnDelete) {
            cancelDelete();
          }
        }}
        cancel={cancelDelete}
        loading={onDeleteLoading}
      />
    </Dialog>
  );
};

function getInfo(resources: ResourceInfoDc[], ignore?: boolean): { name: string; alias: string }[] {
  return ignore ? [] : resources.map(({ name, alias }) => ({ name, alias }));
}
